import * as React from 'react';
import { useId } from '@floating-ui/react';
import clsx from 'clsx';
import { useMediaQuery } from 'react-responsive';
import { Accordion, AccordionContent, AccordionHeader, AccordionItem, AccordionTitle } from '@telia-front/accordion';
import Anchor from '@telia-front/anchor';
import Container from '@telia-front/container';
import Grid, { GridColumn } from '@telia-front/grid';
import Heading from '@telia-front/heading';
import { useCurrentBreakPoint, useIsMounted } from '@telia-front/helper';
import Icon from '@telia-front/icon';
import List, { ListItem } from '@telia-front/list';
import Logo, { DigitalHubLogo } from '@telia-front/logo';
import PropsChecker from '@telia-front/props-checker';
import Separator from '@telia-front/separator';
import { media } from '@telia-front/theme-provider';
const Footer = (props) => (React.createElement(PropsChecker, { componentProps: props, componentName: "Footer", defaultValueName: "defaultOpenColumn", valueName: "openColumn", onChangeNames: "onOpenColumnChange" },
    React.createElement(FooterInner, Object.assign({}, props))));
export default Footer;
const FooterInner = (props) => {
    const { theme = 'dark', color: propColor, align = 'left', logo, digitalHubLogo, bottomLinks, footer, socialMediaItems, columns, onOpenColumnChange, defaultOpenColumn, } = props;
    const footerColumnIdPrefix = 'footer__column-id-';
    const [openColumn, setOpenColumn] = React.useState(defaultOpenColumn || null);
    const currentBreak = useCurrentBreakPoint();
    const isMounted = useIsMounted();
    const isMobileQuery = useMediaQuery({ maxWidth: media.width.sm.max });
    const isMobile = isMounted ? isMobileQuery : false;
    const columnHeadingLevel = logo ? 3 : 2;
    const headingId = useId();
    const currentOpenColumn = onOpenColumnChange && typeof props.openColumn !== 'undefined' ? props.openColumn : openColumn;
    const color = propColor !== null && propColor !== void 0 ? propColor : (theme === 'stealth' ? 'neutral' : undefined);
    const onTitleClick = (clickedColumnId) => {
        const nextOpenColumn = clickedColumnId ? Number(clickedColumnId.replace(footerColumnIdPrefix, '')) : null;
        if (typeof props.openColumn === 'undefined') {
            setOpenColumn(nextOpenColumn);
        }
        if (onOpenColumnChange) {
            onOpenColumnChange(nextOpenColumn);
        }
    };
    const getList = (items, index, isTwoColumn) => (React.createElement(List, { type: "unstyled", "aria-labelledby": `${footerColumnIdPrefix}${index}-title`, className: isTwoColumn ? 'footer__list--two-column' : undefined }, items.map((item, index) => (React.createElement(ListItem, { key: index },
        React.createElement(Anchor, Object.assign({}, item, { isUnderlined: false, className: "footer__link" }), item.label))))));
    const getFooter = (footer) => {
        const getfooterItemWidth = () => {
            if (bottomLinks) {
                return isMobile ? [] : ['xs'];
            }
        };
        return (React.createElement(GridColumn, { width: getfooterItemWidth() },
            React.createElement(List, { direction: isMobile ? undefined : 'inline', type: "unstyled", bordered: !isMobile }, footer.map((item, index) => {
                return (React.createElement(ListItem, { className: clsx('footer__footer-item', item.className), key: index }, item.content));
            }))));
    };
    const getFooterLinks = (links) => {
        return (React.createElement(GridColumn, { width: ['xs'] },
            React.createElement(List, { direction: isMobile ? undefined : 'inline', type: "unstyled" }, links.map((item, index) => {
                return (React.createElement(ListItem, { key: index },
                    React.createElement(Anchor, Object.assign({}, item, { isUnderlined: false, isTextColor: true, className: "text-bold" }), item.label)));
            }))));
    };
    const getSocialMediaLinks = (items) => {
        return items.map((item, index) => {
            return (React.createElement(GridColumn, { key: index, width: ['xs'], align: ['middle-xs'] },
                React.createElement(Anchor, Object.assign({}, item, { className: "footer__link footer__link--social", isUnderlined: false, "aria-label": item.label }),
                    React.createElement(Icon, { modifiers: item.icon, size: "xs", align: "middle" }))));
        });
    };
    const getFooterTop = () => {
        const getSpacing = () => {
            switch (currentBreak) {
                case 'xs':
                    return columns ? 'xsmall' : 'medium-plus';
                case 'sm':
                    return 'small';
                default:
                    return 'medium';
            }
        };
        return (React.createElement(React.Fragment, null,
            React.createElement(Grid, { align: ['between-xs'], className: "footer__top", verticalGutter: "medium" },
                React.createElement(GridColumn, { width: ['xs'], align: ['middle-xs'] }, logo && (React.createElement("h2", { id: headingId },
                    React.createElement(Logo, Object.assign({}, logo, { size: "small" }))))),
                React.createElement(GridColumn, { width: ['xs'], align: ['middle-xs'] },
                    React.createElement(Grid, null,
                        socialMediaItems && getSocialMediaLinks(socialMediaItems),
                        React.createElement(GridColumn, { width: ['xs'], align: ['middle-xs'] }, digitalHubLogo && React.createElement(DigitalHubLogo, Object.assign({}, digitalHubLogo, { color: "neutral", size: "small" })))))),
            React.createElement(Separator, { spacing: getSpacing(), weight: "thin", color: isMobile && columns ? 'invisible' : 'medium' })));
    };
    const getFooterBody = (columns) => (React.createElement(React.Fragment, null, isMobile ? (React.createElement(React.Fragment, null,
        React.createElement(Accordion, { headingLevel: columnHeadingLevel, fullWidth: true, openItem: currentOpenColumn !== null ? `${footerColumnIdPrefix}${currentOpenColumn}` : null, onToggle: onTitleClick }, columns.map((column, index) => {
            return (React.createElement(AccordionItem, { id: `${footerColumnIdPrefix}${index}`, key: index },
                React.createElement(AccordionHeader, null,
                    React.createElement(AccordionTitle, { className: "footer__title h5" }, column.title)),
                React.createElement(AccordionContent, null, getList(column.items, index))));
        })),
        React.createElement(Separator, { color: "invisible", spacing: "xsmall" }))) : (React.createElement(React.Fragment, null,
        React.createElement(Grid, { verticalGutter: "none", horizontalGutter: "medium" }, columns.map((column, index) => {
            return (React.createElement(GridColumn, { width: column.isTwoColumn ? ['md-6'] : ['md-3'], key: index },
                React.createElement(Heading, { level: columnHeadingLevel, className: "footer__title h5", id: `${footerColumnIdPrefix}${index}-title` }, column.title),
                React.createElement("div", { className: "footer__content" }, getList(column.items, index, column.isTwoColumn))));
        })),
        React.createElement(Separator, { weight: "thin", color: "medium", spacing: "medium" })))));
    const BEM = () => {
        const classArray = ['footer', `footer--align-${align}`];
        if (color) {
            classArray.push(`footer--${color}`);
        }
        return classArray.join(' ');
    };
    return (React.createElement("footer", { className: BEM(), "data-theme": theme === 'stealth' ? 'dark' : theme, "aria-labelledby": logo ? headingId : undefined },
        React.createElement(Container, null,
            (logo || socialMediaItems || digitalHubLogo) && getFooterTop(),
            columns && getFooterBody(columns),
            React.createElement("div", { className: "footer__footer" },
                React.createElement(Grid, { align: ['between-xs'], verticalGutter: "small" },
                    footer && getFooter(footer),
                    isMobile && (React.createElement(GridColumn, null,
                        React.createElement(Separator, { color: "invisible" }))),
                    bottomLinks && getFooterLinks(bottomLinks))))));
};
